import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Paper,
  MenuItem, CircularProgress, Pagination, Tooltip,
  Dialog, DialogActions, InputAdornment, LinearProgress,
  FormControl, Select, InputLabel, Alert, Checkbox,
  Slide, Fade,
} from "@mui/material";
import SearchIcon             from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon      from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon     from "@mui/icons-material/CancelOutlined";
import WarningAmberIcon       from "@mui/icons-material/WarningAmber";
import EditNoteIcon           from "@mui/icons-material/EditNote";
import CheckCircleIcon        from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon     from "@mui/icons-material/HourglassEmpty";
import ShoppingCartIcon       from "@mui/icons-material/ShoppingCart";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import CloseIcon              from "@mui/icons-material/Close";
import SelectAllIcon          from "@mui/icons-material/SelectAll";
import DeselectIcon           from "@mui/icons-material/Deselect";
import DeleteSweepIcon        from "@mui/icons-material/DeleteSweep";
import DoneAllIcon            from "@mui/icons-material/DoneAll";
import api from "../../../services/api";

const C = {
  primary:    "#2563EB",
  primaryBg:  "#EFF6FF",
  surface:    "#FFFFFF",
  surfaceAlt: "#F8FAFC",
  border:     "#E2E8F0",
  borderHov:  "#CBD5E1",
  text1:      "#0F172A",
  text2:      "#475569",
  text3:      "#94A3B8",
  success:    "#16A34A",
  successBg:  "#F0FDF4",
  successBdr: "#BBF7D0",
  warn:       "#D97706",
  warnBg:     "#FFFBEB",
  warnBdr:    "#FDE68A",
  danger:     "#DC2626",
  dangerBg:   "#FEF2F2",
  dangerBdr:  "#FECACA",
  infoBdr:    "#BFDBFE",
  neutral:    "#64748B",
  neutralBg:  "#F1F5F9",
  neutralBdr: "#CBD5E1",
};

const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface,
    fontSize: "0.82rem", color: C.text1,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: C.text3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

const thSx = {
  fontSize: "0.7rem", fontWeight: 700, color: C.text3,
  borderBottom: `1px solid ${C.border}`,
  py: "10px", px: "10px",
  textTransform: "uppercase", letterSpacing: "0.04em",
  whiteSpace: "nowrap", backgroundColor: "transparent",
};
const tdSx = {
  py: "10px", px: "10px",
  borderBottom: `1px solid ${C.border}`,
  verticalAlign: "middle",
};

const STATUS_CFG = {
  pending:      { label: "Pending",      color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  dibeli:       { label: "Dibeli",       color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  belum_dibeli: { label: "Belum Dibeli", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};

const TYPE_CFG = {
  biasa:    { label: "Biasa",    color: C.neutral, bg: C.neutralBg, border: C.neutralBdr },
  segera:   { label: "Segera",   color: C.warn,    bg: C.warnBg,    border: C.warnBdr    },
  mendesak: { label: "Mendesak", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr  },
};

function getStatusCfg(val) {
  const k = (val || "").toLowerCase();
  return STATUS_CFG[k] || { label: val || "—", color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" };
}
function getTypeCfg(val) {
  const k = (val || "biasa").toLowerCase();
  return TYPE_CFG[k] || TYPE_CFG.biasa;
}

function StatusChip({ value }) {
  const cfg = getStatusCfg(value);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px", whiteSpace: "nowrap" }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

function TypeChip({ value }) {
  const cfg = getTypeCfg(value);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center",
      px: "8px", py: "3px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px", whiteSpace: "nowrap" }}>
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

function StatCard({ label, value, color, bg, border, icon }) {
  return (
    <Box sx={{ flex: "1 1 130px", backgroundColor: bg, border: `1px solid ${border}`,
      borderRadius: "12px", p: "16px 20px", display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: "9px", backgroundColor: C.surface,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color, lineHeight: 1, letterSpacing: "-0.03em" }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", color: C.text3, mt: 0.3, fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel, variant, loading }) {
  const isError   = variant === "error";
  const isSuccess = variant === "success";
  const accentColor = isError ? C.danger : isSuccess ? C.success : C.warn;
  const accentBg    = isError ? C.dangerBg : isSuccess ? C.successBg : C.warnBg;
  return (
    <Dialog open={open} onClose={onClose}
      PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
      <Box sx={{ p: "24px 24px 0" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: accentBg,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <WarningAmberIcon sx={{ fontSize: 20, color: accentColor }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>{title}</Typography>
            <Typography sx={{ fontSize: "0.8rem", color: C.text2, lineHeight: 1.6 }}>{message}</Typography>
          </Box>
        </Box>
      </Box>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}
          sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
            borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
            "&:hover": { backgroundColor: C.surfaceAlt } }}>Batal</Button>
        <Button onClick={onConfirm} disabled={loading} variant="contained"
          sx={{ textTransform: "none", borderRadius: "8px", px: 2.5,
            backgroundColor: accentColor, fontWeight: 600, boxShadow: "none",
            "&:hover": { backgroundColor: isError ? "#B91C1C" : isSuccess ? "#15803D" : "#B45309", boxShadow: "none" } }}>
          {loading ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BulkActionBar({ selectedIds, pendingCount, onClear, onBulkApprove, onBulkReject, onBulkDelete, bulkLoading }) {
  const count = selectedIds.length;
  return (
    <Slide direction="up" in={count > 0} mountOnEnter unmountOnExit>
      <Box sx={{
        position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
        zIndex: 1300, display: "flex", alignItems: "center", gap: 1.5,
        px: 2.5, py: 1.5, backgroundColor: C.text1, borderRadius: "14px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.22)", minWidth: 440,
        border: "1px solid rgba(255,255,255,0.08)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1,
          px: 1.5, py: 0.5, backgroundColor: C.primary, borderRadius: "8px", flexShrink: 0 }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff", lineHeight: 1 }}>
            {count} dipilih
          </Typography>
        </Box>
        <Box sx={{ flex: 1, height: 1, backgroundColor: "rgba(255,255,255,0.1)" }} />
        {pendingCount > 0 && (
          <Button size="small"
            startIcon={bulkLoading === "approve"
              ? <CircularProgress size={12} sx={{ color: "#fff" }} />
              : <DoneAllIcon sx={{ fontSize: "14px !important" }} />}
            onClick={onBulkApprove} disabled={!!bulkLoading}
            sx={{ textTransform: "none", fontSize: "0.78rem", fontWeight: 600, color: "#fff",
              borderRadius: "8px", height: 32, px: 1.5, backgroundColor: C.success,
              "&:hover": { backgroundColor: "#15803D" } }}>
            {bulkLoading === "approve" ? "Memproses..." : `Dibeli (${pendingCount})`}
          </Button>
        )}
        {pendingCount > 0 && (
          <Button size="small"
            startIcon={bulkLoading === "reject"
              ? <CircularProgress size={12} sx={{ color: "#fff" }} />
              : <CancelOutlinedIcon sx={{ fontSize: "14px !important" }} />}
            onClick={onBulkReject} disabled={!!bulkLoading}
            sx={{ textTransform: "none", fontSize: "0.78rem", fontWeight: 500, color: "#E2E8F0",
              borderRadius: "8px", height: 32, px: 1.5, border: "1px solid rgba(255,255,255,0.15)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)", color: "#fff" } }}>
            {bulkLoading === "reject" ? "Memproses..." : `Belum Dibeli (${pendingCount})`}
          </Button>
        )}
        {pendingCount > 0 && (
          <Button size="small"
            startIcon={bulkLoading === "delete"
              ? <CircularProgress size={12} sx={{ color: "#fff" }} />
              : <DeleteSweepIcon sx={{ fontSize: "14px !important" }} />}
            onClick={onBulkDelete} disabled={!!bulkLoading}
            sx={{ textTransform: "none", fontSize: "0.78rem", fontWeight: 600, color: "#fff",
              borderRadius: "8px", height: 32, px: 1.5, backgroundColor: C.danger,
              "&:hover": { backgroundColor: "#B91C1C" } }}>
            {bulkLoading === "delete" ? "Menghapus..." : `Hapus (${pendingCount})`}
          </Button>
        )}
        {pendingCount === 0 && (
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>
            Tidak ada item pending yang bisa diproses
          </Typography>
        )}
        <Tooltip title="Batal pilih" arrow>
          <IconButton size="small" onClick={onClear}
            sx={{ color: "rgba(255,255,255,0.5)", width: 28, height: 28, borderRadius: "6px",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Slide>
  );
}

function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}
function TruncText({ text, max = 40 }) {
  if (!text) return <Typography sx={{ fontSize: "0.78rem", color: C.text3 }}>—</Typography>;
  const trimmed = text.length > max ? text.slice(0, max) + "…" : text;
  return (
    <Tooltip title={text} arrow placement="top">
      <Typography sx={{ fontSize: "0.78rem", color: C.text2, cursor: "default", maxWidth: 180 }}>{trimmed}</Typography>
    </Tooltip>
  );
}

const ITEMS_PER_PAGE = 10;

export default function ListAtk() {
  const navigate = useNavigate();

  const [data,         setData]         = useState([]);
  const [filtered,     setFiltered]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter,   setTypeFilter]   = useState("all");
  const [page,         setPage]         = useState(1);

  const [deleteDialog,  setDeleteDialog]  = useState({ open: false, id: null, loading: false });
  const [approveDialog, setApproveDialog] = useState({ open: false, id: null, action: null, loading: false });

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(null);

  const paginated        = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const currentPageIds   = paginated.map(p => p.id);
  const allPageSelected  = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.includes(id));
  const somePageSelected = currentPageIds.some(id => selectedIds.includes(id)) && !allPageSelected;

  const selectedPendingIds = selectedIds.filter(id => {
    const item = data.find(d => d.id === id);
    return item?.status?.name?.toLowerCase() === "pending";
  });

  useEffect(() => { setSelectedIds([]); }, [page, search, statusFilter, typeFilter]);

  const handleSelectAll = () => {
    if (allPageSelected) setSelectedIds(prev => prev.filter(id => !currentPageIds.includes(id)));
    else setSelectedIds(prev => [...new Set([...prev, ...currentPageIds])]);
  };
  const handleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res  = await api.get("/atk");
      const d    = res.data;
      const list = Array.isArray(d) ? d
        : Array.isArray(d?.data) ? d.data
        : Array.isArray(d?.data?.data) ? d.data.data : [];
      setData(list);
    } catch { setError("Gagal memuat data pengajuan ATK."); }
    finally  { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    let result = [...data];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.request_number?.toLowerCase().includes(q) ||
        p.requester_name?.toLowerCase().includes(q) ||
        p.department?.toLowerCase().includes(q)     ||
        p.used_for?.toLowerCase().includes(q)       ||
        p.user?.name?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") result = result.filter(p => p.status?.name?.toLowerCase() === statusFilter);
    if (typeFilter   !== "all") result = result.filter(p => (p.request_type || "biasa").toLowerCase() === typeFilter);
    setFiltered(result);
    setPage(1);
  }, [search, statusFilter, typeFilter, data]);

  const handleUpdateStatus = async () => {
    const { id, action } = approveDialog;
    setApproveDialog(p => ({ ...p, loading: true }));
    try {
      await api.put(`/atk/${id}/status`, { status: action });
      await fetchData();
      setApproveDialog({ open: false, id: null, action: null, loading: false });
    } catch {
      setError("Gagal mengubah status pengajuan ATK.");
      setApproveDialog(p => ({ ...p, loading: false }));
    }
  };

  const handleDelete = async () => {
    setDeleteDialog(p => ({ ...p, loading: true }));
    try {
      await api.delete(`/atk/${deleteDialog.id}`);
      setData(p => p.filter(x => x.id !== deleteDialog.id));
      setDeleteDialog({ open: false, id: null, loading: false });
    } catch {
      setError("Gagal menghapus pengajuan ATK.");
      setDeleteDialog(p => ({ ...p, loading: false }));
    }
  };

  const handleBulkApprove = async () => {
    setBulkConfirm(null); setBulkLoading("approve");
    try {
      await api.post("/atk/bulk-status", { ids: selectedPendingIds, status: "dibeli" });
      setSelectedIds([]); await fetchData();
    } catch { setError("Gagal memproses pengajuan ATK yang dipilih."); }
    finally  { setBulkLoading(null); }
  };

  const handleBulkReject = async () => {
    setBulkConfirm(null); setBulkLoading("reject");
    try {
      await api.post("/atk/bulk-status", { ids: selectedPendingIds, status: "belum_dibeli" });
      setSelectedIds([]); await fetchData();
    } catch { setError("Gagal memproses pengajuan ATK yang dipilih."); }
    finally  { setBulkLoading(null); }
  };

  const handleBulkDelete = async () => {
    setBulkConfirm(null); setBulkLoading("delete");
    try {
      await api.post("/atk/bulk-delete", { ids: selectedPendingIds });
      setSelectedIds([]); await fetchData();
    } catch { setError("Gagal menghapus pengajuan ATK yang dipilih."); }
    finally  { setBulkLoading(null); }
  };

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const countBy    = (s) => data.filter(p => p.status?.name?.toLowerCase() === s).length;
  const hasFilter  = search || statusFilter !== "all" || typeFilter !== "all";

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.primaryBg,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <EditNoteIcon sx={{ fontSize: 20, color: C.primary }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: C.text1, lineHeight: 1.2 }}>
              Daftar Pengajuan ATK
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.25 }}>
              Kelola semua pengajuan Alat Tulis Kantor
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stat Cards */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <StatCard label="Total Pengajuan" value={data.length}
          color={C.primary} bg={C.primaryBg} border={C.infoBdr}
          icon={<EditNoteIcon sx={{ fontSize: 18, color: C.primary }} />} />
        <StatCard label="Pending" value={countBy("pending")}
          color={C.warn} bg={C.warnBg} border={C.warnBdr}
          icon={<HourglassEmptyIcon sx={{ fontSize: 18, color: C.warn }} />} />
        <StatCard label="Dibeli" value={countBy("dibeli")}
          color={C.success} bg={C.successBg} border={C.successBdr}
          icon={<ShoppingCartIcon sx={{ fontSize: 18, color: C.success }} />} />
        <StatCard label="Belum Dibeli" value={countBy("belum_dibeli")}
          color={C.danger} bg={C.dangerBg} border={C.dangerBdr}
          icon={<RemoveShoppingCartIcon sx={{ fontSize: 18, color: C.danger }} />} />
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}
          sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", border: `1px solid ${C.dangerBdr}` }}>
          {error}
        </Alert>
      )}

      {/* Filter Bar */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px",
        backgroundColor: C.surface, mb: 2 }}>
        <Box sx={{ p: "12px 16px", display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <TextField size="small" placeholder="Cari no. pengajuan, pemohon, unit..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: C.text3 }} /></InputAdornment> }}
            sx={{ ...inputBase, flex: "1 1 220px", maxWidth: 320 }} />
          <FormControl size="small" sx={{ minWidth: 160, ...inputBase }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all"><em>Semua Status</em></MenuItem>
              {Object.entries(STATUS_CFG).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: v.dot }} />
                    {v.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150, ...inputBase }}>
            <InputLabel>Sifat</InputLabel>
            <Select value={typeFilter} label="Sifat" onChange={(e) => setTypeFilter(e.target.value)}>
              <MenuItem value="all"><em>Semua Sifat</em></MenuItem>
              {Object.entries(TYPE_CFG).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Typography sx={{ fontSize: "0.82rem", color: v.color, fontWeight: 600 }}>{v.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasFilter && (
            <Button size="small" onClick={() => { setSearch(""); setStatusFilter("all"); setTypeFilter("all"); }}
              sx={{ fontSize: "0.78rem", color: C.danger, textTransform: "none", p: "0 6px", minWidth: 0,
                "&:hover": { backgroundColor: C.dangerBg } }}>Reset</Button>
          )}
          <Typography sx={{ ml: "auto", fontSize: "0.75rem", color: C.text3, fontWeight: 500, whiteSpace: "nowrap" }}>
            {filtered.length} pengajuan
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ px: 2, py: "10px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: `1px solid ${C.border}`,
          backgroundColor: selectedIds.length > 0 ? C.primaryBg : C.surfaceAlt, transition: "background 0.2s" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.text3, fontWeight: 500 }}>
              {loading ? "Memuat data..." : paginated.length > 0
                ? `${(page - 1) * ITEMS_PER_PAGE + 1}–${Math.min(page * ITEMS_PER_PAGE, filtered.length)} dari ${filtered.length}`
                : "0 pengajuan"}
            </Typography>
            {selectedIds.length > 0 && (
              <Fade in>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: C.primary }} />
                  <Typography sx={{ fontSize: "0.75rem", color: C.primary, fontWeight: 600 }}>
                    {selectedIds.length} item dipilih
                  </Typography>
                  <Button size="small" onClick={() => setSelectedIds([])}
                    sx={{ fontSize: "0.7rem", color: C.text3, textTransform: "none", p: "0 4px", minWidth: 0,
                      "&:hover": { color: C.danger } }}>Batalkan</Button>
                </Box>
              </Fade>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {loading && <LinearProgress sx={{ width: 80, height: 3, borderRadius: 4,
              backgroundColor: C.border, "& .MuiLinearProgress-bar": { backgroundColor: C.primary } }} />}
            {!loading && paginated.length > 0 && (
              <Tooltip title={allPageSelected ? "Batal pilih halaman ini" : "Pilih semua di halaman ini"} arrow>
                <IconButton size="small" onClick={handleSelectAll}
                  sx={{ width: 28, height: 28, borderRadius: "6px",
                    color: allPageSelected ? C.primary : C.text3,
                    backgroundColor: allPageSelected ? C.primaryBg : "transparent",
                    border: `1px solid ${allPageSelected ? C.primary : C.border}`,
                    "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg } }}>
                  {allPageSelected ? <DeselectIcon sx={{ fontSize: 14 }} /> : <SelectAllIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                <TableCell sx={{ ...thSx, width: 44, px: "8px" }}>
                  <Checkbox size="small" checked={allPageSelected} indeterminate={somePageSelected}
                    onChange={handleSelectAll} disabled={loading || paginated.length === 0}
                    sx={{ p: 0.5, color: C.text3, "&.Mui-checked": { color: C.primary },
                      "&.MuiCheckbox-indeterminate": { color: C.primary } }} />
                </TableCell>
                {["No", "No. Pengajuan", "Nama Pemohon", "Departemen / Unit",
                  "Digunakan Untuk", "Tgl. Pengajuan", "Sifat", "Item",
                  "Catatan", "Status", "Aksi"].map(h => (
                  <TableCell key={h} sx={thSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 8, border: 0 }}>
                    <CircularProgress size={28} thickness={4} sx={{ color: C.primary }} />
                    <Typography sx={{ mt: 1.5, fontSize: "0.82rem", color: C.text3 }}>Memuat data...</Typography>
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 8, border: 0 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "10px", backgroundColor: C.surfaceAlt,
                      display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <EditNoteIcon sx={{ fontSize: 22, color: C.text3 }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.875rem", color: C.text2, fontWeight: 500 }}>
                      {hasFilter ? "Tidak ada pengajuan ATK yang sesuai filter" : "Belum ada pengajuan ATK"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.5 }}>
                      {hasFilter ? "Coba ubah atau reset filter" : "Belum ada pengajuan ATK dari user"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginated.map((p, idx) => {
                const isPending  = p.status?.name?.toLowerCase() === "pending";
                const isSelected = selectedIds.includes(p.id);
                const rowNum     = (page - 1) * ITEMS_PER_PAGE + idx + 1;
                return (
                  <TableRow key={p.id} sx={{
                    "&:hover": { backgroundColor: isSelected ? "#EFF6FF" : C.surfaceAlt },
                    "&:hover .row-actions": { opacity: 1 },
                    backgroundColor: isSelected ? "#F0F7FF" : "transparent",
                    transition: "background 0.12s",
                  }}>
                    <TableCell sx={{ ...tdSx, px: "8px", width: 44 }}>
                      <Checkbox size="small" checked={isSelected} onChange={() => handleSelectOne(p.id)}
                        sx={{ p: 0.5, color: C.text3, "&.Mui-checked": { color: C.primary } }} />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.72rem", color: C.text3,
                        fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{rowNum}</Typography>
                    </TableCell>
                    {/* No. Pengajuan — plain text, no expand */}
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: C.primary,
                        fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" }}>
                        {p.request_number || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.82rem", color: C.text1, fontWeight: 600, whiteSpace: "nowrap" }}>
                        {p.requester_name || "—"}
                      </Typography>
                      {p.user?.name && p.user.name !== p.requester_name && (
                        <Typography sx={{ fontSize: "0.7rem", color: C.text3, mt: 0.25 }}>via {p.user.name}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.8rem", color: C.text2, fontWeight: 500, whiteSpace: "nowrap" }}>
                        {p.department || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}><TruncText text={p.used_for} max={35} /></TableCell>
                    <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>{fmtDate(p.request_date)}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}><TypeChip value={p.request_type} /></TableCell>
                    <TableCell sx={tdSx}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                        minWidth: 30, height: 22, px: 1, borderRadius: "6px",
                        backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }}>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: C.text1,
                          fontVariantNumeric: "tabular-nums" }}>{p.items?.length ?? 0}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={tdSx}><TruncText text={p.description} max={35} /></TableCell>
                    <TableCell sx={tdSx}><StatusChip value={p.status?.name} /></TableCell>
                    <TableCell sx={tdSx}>
                      <Box className="row-actions" sx={{ display: "flex", gap: 0.5,
                        opacity: { xs: 1, md: 0.4 }, transition: "opacity 0.15s" }}>
                        <Tooltip title="Lihat Detail" arrow>
                          <IconButton size="small" onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/atk/${p.id}`)}
                            sx={{ width: 28, height: 28, borderRadius: "6px", color: C.text3,
                              "&:hover": { color: C.primary, backgroundColor: C.primaryBg } }}>
                            <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        {isPending && (
                          <Tooltip title="Tandai Semua Dibeli" arrow>
                            <IconButton size="small"
                              onClick={() => setApproveDialog({ open: true, id: p.id, action: "dibeli", loading: false })}
                              sx={{ width: 28, height: 28, borderRadius: "6px", color: C.text3,
                                "&:hover": { color: C.success, backgroundColor: C.successBg } }}>
                              <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isPending && (
                          <Tooltip title="Tandai Semua Belum Dibeli" arrow>
                            <IconButton size="small"
                              onClick={() => setApproveDialog({ open: true, id: p.id, action: "belum_dibeli", loading: false })}
                              sx={{ width: 28, height: 28, borderRadius: "6px", color: C.text3,
                                "&:hover": { color: C.warn, backgroundColor: C.warnBg } }}>
                              <CancelOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isPending && (
                          <Tooltip title="Hapus" arrow>
                            <IconButton size="small"
                              onClick={() => setDeleteDialog({ open: true, id: p.id, loading: false })}
                              sx={{ width: 28, height: 28, borderRadius: "6px", color: C.text3,
                                "&:hover": { color: C.danger, backgroundColor: C.dangerBg } }}>
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {!loading && totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            px: 2, py: "10px", borderTop: `1px solid ${C.border}`, backgroundColor: C.surfaceAlt }}>
            <Typography sx={{ fontSize: "0.75rem", color: C.text3 }}>Halaman {page} dari {totalPages}</Typography>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small" color="primary"
              sx={{ "& .MuiPaginationItem-root": { fontSize: "0.75rem", borderRadius: "6px", minWidth: 28, height: 28 },
                "& .Mui-selected": { backgroundColor: `${C.primary} !important`, color: "#fff", fontWeight: 600 } }} />
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      <ConfirmDialog open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, loading: false })}
        onConfirm={handleDelete} loading={deleteDialog.loading}
        title="Hapus Pengajuan ATK?" confirmLabel="Hapus" variant="error"
        message="Pengajuan ATK ini akan dihapus secara permanen. Hanya pengajuan berstatus Pending yang bisa dihapus." />

      <ConfirmDialog open={approveDialog.open}
        onClose={() => setApproveDialog({ open: false, id: null, action: null, loading: false })}
        onConfirm={handleUpdateStatus} loading={approveDialog.loading}
        title={approveDialog.action === "dibeli" ? "Tandai Semua Item Dibeli?" : "Tandai Semua Item Belum Dibeli?"}
        message={approveDialog.action === "dibeli"
          ? "Semua item pada pengajuan ATK ini akan ditandai sebagai Dibeli."
          : "Semua item pada pengajuan ATK ini akan ditandai sebagai Belum Dibeli."}
        confirmLabel={approveDialog.action === "dibeli" ? "Ya, Dibeli" : "Ya, Belum Dibeli"}
        variant={approveDialog.action === "dibeli" ? "success" : "warning"} />

      <ConfirmDialog open={bulkConfirm === "approve"} onClose={() => setBulkConfirm(null)}
        onConfirm={handleBulkApprove} loading={bulkLoading === "approve"}
        title={`Tandai Dibeli ${selectedPendingIds.length} Pengajuan ATK?`}
        message="Semua item pada pengajuan ATK pending yang dipilih akan ditandai Dibeli."
        confirmLabel="Ya, Dibeli Semua" variant="success" />

      <ConfirmDialog open={bulkConfirm === "reject"} onClose={() => setBulkConfirm(null)}
        onConfirm={handleBulkReject} loading={bulkLoading === "reject"}
        title={`Tandai Belum Dibeli ${selectedPendingIds.length} Pengajuan ATK?`}
        message="Semua item pada pengajuan ATK pending yang dipilih akan ditandai Belum Dibeli."
        confirmLabel="Ya, Belum Dibeli Semua" variant="warning" />

      <ConfirmDialog open={bulkConfirm === "delete"} onClose={() => setBulkConfirm(null)}
        onConfirm={handleBulkDelete} loading={bulkLoading === "delete"}
        title={`Hapus ${selectedPendingIds.length} Pengajuan ATK?`}
        message="Semua pengajuan ATK pending yang dipilih akan dihapus secara permanen."
        confirmLabel="Hapus Semua" variant="error" />

      <BulkActionBar selectedIds={selectedIds} pendingCount={selectedPendingIds.length}
        onClear={() => setSelectedIds([])}
        onBulkApprove={() => setBulkConfirm("approve")}
        onBulkReject={() => setBulkConfirm("reject")}
        onBulkDelete={() => setBulkConfirm("delete")}
        bulkLoading={bulkLoading} />
    </Box>
  );
}